import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, ChevronDown, ShieldCheck } from "lucide-react";
import { Logo } from "./Logo";
import { BRAND } from "@/lib/brand";
import { useContent } from "@/lib/cms";

const DEFAULT_LINKS = [
  { label: "Product", href: "#features" },
  { label: "Industries", href: "#industries" },
  { label: "Integrations", href: "#integrations" },
  { label: "Pricing", href: "#pricing" },
  { label: "Compare", href: "#comparison" },
];

export const Navbar = ({ onBookDemo }) => {
  const [scrolled, setScrolled] = useState(false);
  const { data: nav } = useContent("navigation", {});
  const links = nav.links && nav.links.length ? nav.links : DEFAULT_LINKS;
  const loginCfg = nav.login || { label: "Log in", href: BRAND.links.login };
  const bookCfg = nav.book_demo || { label: "Book a Demo", href: BRAND.links.bookDemo };
  const location = useLocation();
  const navigate = useNavigate();
  const isLanding = location.pathname === "/";

  // Handle hash link clicks: if not on landing page, navigate to /#hash first
  const handleHashClick = (e, href) => {
    if (!href || !href.startsWith("#")) return;
    if (!isLanding) {
      e.preventDefault();
      navigate("/" + href);
    }
    // If already on landing page, default anchor behavior works fine
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass shadow-sm" : "bg-white/40 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo />
          </div>

        <nav className="hidden lg:flex items-center gap-2">
          {links.map((l) => (
            <a
              key={l.label}
              href={isLanding ? l.href : (l.href.startsWith("#") ? "/" + l.href : l.href)}
              onClick={(e) => handleHashClick(e, l.href)}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 rounded-full hover:bg-slate-100 transition-colors flex items-center gap-1"
            >
              {l.label}
              {l.label === "Product" }
            </a>
          ))}
          <Link
            to="/blog"
            data-testid="nav-link-blog"
            className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 rounded-full hover:bg-slate-100 transition-colors"
          >
            Blog
          </Link>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={loginCfg.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-login-btn"
            className="inline-flex items-center justify-center whitespace-nowrap rounded-full h-10 px-4 text-sm font-semibold text-slate-700 hover:text-emerald-700 hover:bg-emerald-50 active:bg-emerald-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 transition-colors"
          >
            {loginCfg.label}
          </a>
          <Link
            to="/whatsapp-onboarding"
            data-testid="navbar-onboarding-btn"
            className="inline-flex items-center gap-1.5 justify-center whitespace-nowrap rounded-full h-10 px-4 text-sm font-semibold text-slate-800 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all"
          >
            <ShieldCheck className="h-4 w-4 text-emerald-600" /> Complete Onboarding
          </Link>
          <a
            href={bookCfg.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-book-demo-btn"
            className="inline-flex items-center justify-center whitespace-nowrap rounded-full h-10 px-5 text-sm font-semibold bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white shadow-sm hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 transition-all"
          >
            {bookCfg.label}
          </a>
        </div>

        <Sheet>
          <SheetTrigger asChild className="lg:hidden">
            <Button variant="ghost" size="icon" aria-label="Open menu" data-testid="navbar-mobile-menu-btn">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[min(300px,calc(100vw-2rem))]">
            <div className="mt-8 flex flex-col gap-2">
              {links.map((l) => (
                <a
                  key={l.label}
                  href={isLanding ? l.href : (l.href.startsWith("#") ? "/" + l.href : l.href)}
                  onClick={(e) => handleHashClick(e, l.href)}
                  className="px-3 py-3 text-base font-medium rounded-lg hover:bg-slate-100"
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </a>
              ))}
              <Link
                to="/blog"
                className="px-3 py-3 text-base font-medium rounded-lg hover:bg-slate-100"
                data-testid="mobile-nav-blog"
              >
                Blog
              </Link>
              <a
                href={BRAND.links.login}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-login-btn"
                className="mt-2 w-full inline-flex items-center justify-center rounded-full h-11 px-4 text-sm font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                Log in
              </a>
              <Link
                to="/whatsapp-onboarding"
                data-testid="mobile-onboarding-btn"
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-full h-11 px-4 text-sm font-semibold bg-slate-900 hover:bg-slate-800 text-white"
              >
                <ShieldCheck className="h-4 w-4" /> Complete Onboarding
              </Link>
              <a
                href={BRAND.links.bookDemo}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-book-demo-btn"
                className="w-full inline-flex items-center justify-center rounded-full h-11 px-4 text-sm font-semibold bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white"
              >
                Book a Demo
              </a>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};
